import React from 'react';
import { Icon } from '@noths/polaris-design-system-core-components';
import { useRouter } from 'next/router';

import type {
  FacetOptionWithResetHref,
  FacetWithResetHrefs,
} from 'src/adapters/storeToPageURL/adaptStoreToUIFacetHrefs';
import * as styles from './ActiveFacets.styles';

/** An applied condition that no facet holds, such as a guaranteed arrival day. */
export interface ActiveCondition {
  /** The page with the condition removed. */
  href: string;
  title: string;
}

interface ActiveFacetsProps {
  activeFacets: FacetWithResetHrefs[];
  clearAllHref?: string;
  conditions?: ActiveCondition[];
}

export const ActiveFacets = ({
  activeFacets,
  clearAllHref,
  conditions = [],
}: ActiveFacetsProps) => {
  const router = useRouter();
  const navigate = (href: string) => router.push(href, undefined, { shallow: true, scroll: false });

  const activeFacetOptions = activeFacets.reduce((acc, facet) => {
    facet.options.forEach((option) => {
      const href = option.href;

      if (option.active && href) {
        acc.push({ facet, option: { ...option, href } });
      }
    });

    return acc;
  }, [] as { facet: FacetWithResetHrefs; option: FacetOptionWithResetHref & { href: string } }[]);

  if (activeFacets.length === 0) {
    return null;
  }

  return (
    <ul css={styles.list}>
      {[
        ...activeFacetOptions.map(({ facet, option }) => ({
          href: option.href,
          key: `${facet.key}-${option.value}`,
          title: option.title,
        })),
        ...conditions.map((condition) => ({ ...condition, key: condition.title })),
      ].map(({ href, key, title }) => (
        <li key={key}>
          <button
            aria-label={`Clear the "${title}" filter`}
            css={styles.resetFacetButton}
            data-href={href}
            onClick={() => {
              navigate(href);
            }}
            type="button"
          >
            {title} <Icon name="close" size="sm" />
          </button>
        </li>
      ))}
      {clearAllHref && (
        <li>
          <button
            aria-label="Clear all filters"
            css={styles.resetAllButton}
            data-href={clearAllHref}
            onClick={() => {
              navigate(clearAllHref);
            }}
            type="button"
          >
            Clear all
          </button>
        </li>
      )}
    </ul>
  );
};
